import type { SearchResult, SearchResultType } from "@/types/v2/search"

// localStorage-backed "recents" for the command palette
// cold-start view (CON-183):
//
//   1. RECENT — entities the user recently opened from the
//      palette (a compact projection of `SearchResult`).
//   2. RECENT SEARCHES — query strings the user recently
//      ran.
//
// Both are capped, most-recent-first, de-duplicated, and
// fully guarded for SSR / jsdom-without-localStorage so the
// palette never throws while reading them.

const RECENT_ITEMS_KEY = "conecto:cmdk:recent-items"
const RECENT_SEARCHES_KEY = "conecto:cmdk:recent-searches"

export const RECENTS_CAP = 6

// The compact shape we persist per opened entity. A subset
// of `SearchResult` — enough to re-render the row in the
// cold-start list and navigate on click — plus an `openedAt`
// stamp the row renders as relative time. We deliberately do
// NOT persist `snippet` (a stale match excerpt would mislead
// outside its original query) or `previewTarget` (the cold
// list is navigate-only, no preview pane).
export interface RecentItem {
  key: string
  type: SearchResultType
  title: string
  subtitle: string
  href: string
  // ISO timestamp of when it was opened.
  openedAt: string
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null
    return window.localStorage
  } catch {
    // Access can throw in sandboxed iframes / privacy modes.
    return null
  }
}

function read<T>(key: string): T[] {
  const storage = safeStorage()
  if (!storage) return []
  try {
    const raw = storage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

function write<T>(key: string, value: T[]): void {
  const storage = safeStorage()
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota / disabled storage — drop silently. Recents are
    // a nicety, never load-bearing.
  }
}

// ---- Recent items -------------------------------------

export function getRecentItems(): RecentItem[] {
  return read<RecentItem>(RECENT_ITEMS_KEY).filter(isValidRecentItem)
}

// Record an opened entity. De-dupes on `key` (re-opening an
// item floats it to the top, not a duplicate row), stamps
// `openedAt`, and caps the list.
export function pushRecentItem(
  result: Pick<
    SearchResult,
    "key" | "type" | "title" | "subtitle" | "href"
  >,
  now: Date = new Date(),
): void {
  const entry: RecentItem = {
    key: result.key,
    type: result.type,
    title: result.title,
    subtitle: result.subtitle,
    href: result.href,
    openedAt: now.toISOString(),
  }
  const existing = getRecentItems().filter((item) => item.key !== entry.key)
  write(RECENT_ITEMS_KEY, [entry, ...existing].slice(0, RECENTS_CAP))
}

export function clearRecentItems(): void {
  write<RecentItem>(RECENT_ITEMS_KEY, [])
}

function isValidRecentItem(value: unknown): value is RecentItem {
  if (!value || typeof value !== "object") return false
  const v = value as Record<string, unknown>
  return (
    typeof v.key === "string" &&
    typeof v.type === "string" &&
    typeof v.title === "string" &&
    typeof v.href === "string"
  )
}

// ---- Recent searches ----------------------------------

export function getRecentSearches(): string[] {
  return read<string>(RECENT_SEARCHES_KEY).filter(
    (q): q is string => typeof q === "string" && q.trim().length > 0,
  )
}

// Record a query the user actually ran. De-dupes
// case-insensitively (so "Coca-Cola" and "coca-cola" don't
// both linger), floating the latest spelling to the top.
export function pushRecentSearch(query: string): void {
  const trimmed = query.trim()
  if (!trimmed) return
  const lower = trimmed.toLowerCase()
  const existing = getRecentSearches().filter(
    (q) => q.toLowerCase() !== lower,
  )
  write(RECENT_SEARCHES_KEY, [trimmed, ...existing].slice(0, RECENTS_CAP))
}

export function clearRecentSearches(): void {
  write<string>(RECENT_SEARCHES_KEY, [])
}
